import { useGetOrganizationsByUserQuery } from '@carboncopyco/data';
import { CloseOutlineIcon, SearchOutlineIcon } from '@carboncopyco/icons';
import type { IOrganizationData } from '@carboncopyco/types';
import { Avatar, Button, ColorHex, TextBase } from '@carboncopyco/ui';
import { useRef, useState } from 'react';
import { convertKeysToSnakeCase } from '../../../util/newObjectSystemHelper';
import { useTabManager } from '../../services/TabManager';
import { useUser } from '../../store/context';

const OrganizationAutocompleteModal = ({
  triggerModal,
  visible,
  setActiveOrganization,
}: {
  visible: boolean;
  triggerModal: () => void;
  setActiveOrganization: (organization: any) => void;
}) => {
  const { closeAllGlobalTabs } = useTabManager();
  const { data: orgsData, isLoading: isOrgsLoading } =
    useGetOrganizationsByUserQuery(undefined, {
      refetchOnFocus: true,
    });
  const { activeOrganization } = useUser();
  const [value, setValue] = useState('');
  const [isSearchEnabled, setIsSearchEnabled] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const handleOnChangeValue = (e: any) => {
    setValue(e.target.value);
  };

  const handleChangeOrganization = (org: IOrganizationData) => {
    closeAllGlobalTabs();
    setActiveOrganization(convertKeysToSnakeCase(org));
    triggerModal();
  };

  const handleClearSearch = () => {
    setValue('');
    inputRef.current?.focus();
    setIsSearchEnabled(true);
  };

  const handleCloseSearch = () => {
    setValue('');
    triggerModal();
  };

  const handleOnFocus = () => {
    if (!value.length) {
      setIsSearchEnabled(true);
    }
  };

  const handleOnBlur = () => {
    if (!value.length) {
      setIsSearchEnabled(false);
    }
  };

  const filteredOrganizationsByValue = () =>
    orgsData?.filter(
      (org) =>
        (org?.active &&
          !org?.isDeleted &&
          org?.organizationName?.toLowerCase().includes(value.toLowerCase())) ||
        org?.id === Number(value)
    ) || [];

  return visible || !isOrgsLoading ? (
    <div className="flex flex-col py-2 w-ds-500 bg-white h-auto absolute px-2 mx-auto my-auto left-0 right-0 top-5  rounded-md shadow-ds-lg">
      <div className="flex justify-between px-2">
        <div className="flex justify-start items-center">
          {activeOrganization ? (
            <>
              <SearchOutlineIcon
                size={20}
                color={ColorHex.natural300}
              />
              {!isSearchEnabled && (
                <div
                  className="pl-2"
                  data-testid="org-avatar"
                >
                  <Avatar
                    initials={activeOrganization?.organization_name[0]}
                    image={activeOrganization?.icon}
                  />
                </div>
              )}
            </>
          ) : null}
          <input
            ref={inputRef}
            onFocus={handleOnFocus}
            onBlur={handleOnBlur}
            className="pl-2 w-ds-sm-modal border-none outline-none focus:outline-none focus:border-0 focus:ring-0"
            type="text"
            value={value}
            onChange={handleOnChangeValue}
            placeholder={
              isSearchEnabled ? '' : activeOrganization?.organization_name
            }
          />
        </div>
        <div className="flex justify-start items-center cursor-pointer">
          <Button
            link
            secondary
            label="Clear"
            onClick={handleClearSearch}
          />
          {/* <Separator middle /> */}
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions,jsx-a11y/click-events-have-key-events */}
          <div
            className="pl-2"
            onClick={handleCloseSearch}
          >
            <CloseOutlineIcon
              size={20}
              color={ColorHex.natural300}
            />
          </div>
        </div>
      </div>
      {isSearchEnabled && value.length > 1 && (
        <div data-testid="org-list">
          <div className="py-3">{/* <Separator horizontal /> */}</div>
          <div>
            {filteredOrganizationsByValue().map((org) => (
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
              <div
                key={org.id}
                onClick={() => handleChangeOrganization(org)}
                className="flex items-center py-2 cursor-pointer rounded-md hover:bg-gray-50 "
                data-testid="org-list-item"
              >
                <div className="pr-2 px-2">
                  <Avatar
                    initials={org?.organizationName[0]}
                    image={org.icon}
                  />
                </div>
                <TextBase
                  label={org?.organizationName}
                  color="text-ds-natural-300"
                />
              </div>
            ))}
            {!filteredOrganizationsByValue().length && value.length > 2 && (
              <div className="text-center">
                <TextBase
                  ellipsis
                  label={`No matches found for "${value}"`}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  ) : null;
};

export default OrganizationAutocompleteModal;
